import BaseApiClient from '../BaseApiClient';
import { SENTIMENT_CONFIG_API_VERSION, sentimentConfigApiPaths } from './paths';
import type {
  CreateSentimentConfigPayload,
  SentimentConfigLabel,
  SentimentConfiguration,
  TaxonomyAspect,
  UpdateSentimentConfigPayload
} from './types';

export type {
  CreateSentimentConfigPayload,
  SentimentConfigLabel,
  SentimentConfiguration,
  TaxonomyAspect,
  UpdateSentimentConfigPayload
} from './types';

export { isSentimentConfigLabel, isSystemSentimentConfig } from './types';
export { sentimentConfigurationQueryKeys } from './query-keys';

export {
  useSentimentConfigurationsQuery,
  useSentimentConfigurationQuery,
  useTaxonomyAspectsQuery
} from './use-sentiment-configuration-queries';

export {
  useCreateSentimentConfigMutation,
  useUpdateSentimentConfigMutation,
  useDeleteSentimentConfigMutation,
  useRestoreSentimentConfigMutation
} from './use-sentiment-configuration-mutations';

function unwrapData<T>(payload: { data?: T } | T): T {
  if (
    payload !== null &&
    typeof payload === 'object' &&
    'data' in payload &&
    (payload as { data?: T }).data !== undefined
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export class ApiSentimentConfiguration extends BaseApiClient {
  private withVersionHeader() {
    return { headers: { Version: SENTIMENT_CONFIG_API_VERSION } };
  }

  async getAll(): Promise<SentimentConfiguration[]> {
    const res = await this.client.get(
      sentimentConfigApiPaths.sentimentConfigs,
      this.withVersionHeader()
    );
    return unwrapData<SentimentConfiguration[]>(res.data);
  }

  async getById(id: string): Promise<SentimentConfiguration> {
    const res = await this.client.get(
      sentimentConfigApiPaths.sentimentConfig(id),
      this.withVersionHeader()
    );
    return unwrapData<SentimentConfiguration>(res.data);
  }

  async create(
    payload: CreateSentimentConfigPayload
  ): Promise<SentimentConfiguration> {
    const res = await this.client.post(
      sentimentConfigApiPaths.sentimentConfigs,
      payload,
      this.withVersionHeader()
    );
    return unwrapData<SentimentConfiguration>(res.data);
  }

  async update(
    id: string,
    payload: UpdateSentimentConfigPayload
  ): Promise<SentimentConfiguration> {
    const res = await this.client.put(
      sentimentConfigApiPaths.sentimentConfig(id),
      payload,
      this.withVersionHeader()
    );
    return unwrapData<SentimentConfiguration>(res.data);
  }

  async delete(id: string): Promise<void> {
    await this.client.delete(
      sentimentConfigApiPaths.sentimentConfig(id),
      this.withVersionHeader()
    );
  }

  async restore(uuid: string): Promise<SentimentConfiguration> {
    const res = await this.client.post(
      sentimentConfigApiPaths.restoreSentimentConfig(uuid),
      {},
      this.withVersionHeader()
    );
    return unwrapData<SentimentConfiguration>(res.data);
  }

  async getTaxonomyAspects(): Promise<TaxonomyAspect[]> {
    const res = await this.client.get(
      sentimentConfigApiPaths.taxonomyAspects,
      this.withVersionHeader()
    );
    return unwrapData<TaxonomyAspect[]>(res.data);
  }
}
